import { observer } from 'mobx-react-lite';
import { default as FeedbackContainer, FeedbackType } from './FeedbackContainer';
import RelatedContentSender from './RelatedContentSender';
import { PersonaSize } from '@fluentui/react/lib/Persona';
import { KeyCodes } from '@fluentui/react/lib/Utilities';
import type { ReadingPaneEntrySourceType } from 'owa-calendar-helpers-types/lib/ReadingPaneEntrySourceType';
import type ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { ControlIcons } from 'owa-control-icons';
import { Icon } from '@fluentui/react/lib/Icon';
import loc, { format } from 'owa-localize';
import {
    EventInsightType,
    InsightsDataFetchSource,
    AttendeeType,
} from 'owa-meeting-insights-types-and-flights';
import { getInsightsTraceId, lazyOpenEventInsight } from 'owa-meeting-insights';
import {
    atLabelSentenceCase,
    eventDurationLabel,
    attendeesLabel,
    notShownAttendeesLabel,
} from 'owa-meeting-insights/lib/components/InsightDocRow.locstring.json';
import { getEventInsightAriaLabel } from 'owa-meeting-insights/lib/helpers/ariaLabelHelpers';
import { getIsOrganizerFromMeetingId } from 'owa-meeting-insights/lib/selectors/instrumentationSelectors';
import { isFeatureEnabled } from 'owa-feature-flags';
import { trace } from 'owa-trace';
import * as React from 'react';

import styles from './RelatedEvent.scss';
import classnamesBind from 'classnames/bind';
const classNames = classnamesBind.bind(styles);

export interface RelatedEventProps {
    meetingId: string;
    event: EventInsightType;
    colorScheme: ColorScheme;
    color: string;
    readingPaneEntrySource?: ReadingPaneEntrySourceType; // null when rendering from CITI card
    insightsEntrySource: InsightsDataFetchSource;
    position: number;
    renderedEventsCount: number;
}

export default observer(function RelatedEvent(props: RelatedEventProps) {
    const renderFirstRow = (): JSX.Element => {
        const { senderName, senderEmailAddress, start, duration } = props.event;
        const timestampToDisplay = format(loc(atLabelSentenceCase), start);
        return (
            <div className={styles.firstRow}>
                <div className={styles.firstColumn}>
                    {senderName != '' ? (
                        <RelatedContentSender
                            emailAddress={senderEmailAddress}
                            name={senderName}
                            size={PersonaSize.size24}
                            personaType={'NotResolved'}
                        />
                    ) : null}
                </div>
                <div className={styles.secondColumn}>
                    <span className={styles.senderName} title={senderName || senderEmailAddress}>
                        {senderName || senderEmailAddress}
                    </span>
                    <span className={styles.timestamp} title={timestampToDisplay}>
                        {timestampToDisplay}
                    </span>
                    <span className={styles.duration} title={duration}>
                        {format(loc(eventDurationLabel, duration))}
                    </span>
                </div>
            </div>
        );
    };
    const renderSecondRow = (): JSX.Element => {
        return (
            <div className={styles.secondRow}>
                <span className={classNames(styles.subject)} title={props.event.subject}>
                    {props.event.subject}
                </span>
            </div>
        );
    };
    const renderThirdRow = (): JSX.Element[] => {
        let retElements: JSX.Element[] = [];
        let count: number = 0;
        let displayLimit = 2;

        retElements.push(<div className={styles.attendeesContainer}> {loc(attendeesLabel)} </div>);

        props.event.attendees.forEach(element => {
            let attendee = element as AttendeeType;
            if (attendee && count < displayLimit) {
                retElements.push(createAttendeeDiv(attendee));
            }
            count = count + 1;
        });

        if (count > displayLimit) {
            const hiddenAttendeesCount = count - displayLimit;
            retElements.push(
                <div className={styles.attendeesContainer}>
                    {format(loc(notShownAttendeesLabel, hiddenAttendeesCount))}
                </div>
            );
        }

        return retElements;
    };

    const geStatusIconName = (status: string): ControlIcons => {
        switch (status) {
            case 'Accepted':
                return ControlIcons.StatusCircleCheckmark;
            case 'Declined':
                return ControlIcons.StatusCircleErrorX;
            case 'Tentative':
                return ControlIcons.StatusCircleQuestionMark;
            default:
                return ControlIcons.EventInfo;
        }
    };

    const createAttendeeDiv = (attendee: AttendeeType) => {
        const name = attendee.name;
        const emailAddress = attendee.emailAddress;
        const responseStatus = attendee.response;
        const iconName: ControlIcons = geStatusIconName(responseStatus);
        return (
            <div className={styles.thirdRow}>
                <div className={styles.firstColumn}>
                    {<Icon iconName={iconName} />}
                    {name != '' ? (
                        <RelatedContentSender
                            emailAddress={emailAddress}
                            name={name}
                            size={PersonaSize.size24}
                            personaType={'NotResolved'}
                        />
                    ) : null}
                    <span className={styles.senderName} title={name || emailAddress}>
                        {name || emailAddress}
                    </span>
                </div>
            </div>
        );
    };

    const renderReasonMarkerRow = (): JSX.Element => {
        const senderName = props.event.senderName;
        const reasonMarker = props.event.reasonMarker
            ? props.event.reasonMarker
            : 'Shared by ' + senderName + '.';

        // VSO 109482: Update code to handle new API with parameters and reasonCodes to generate localized strings;
        return (
            <div className={styles.reasonMarkerRow}>
                <span className={styles.reasonMarker}>{reasonMarker}</span>
            </div>
        );
    };

    const relatedEventContainerRef = React.useRef<HTMLDivElement>();

    const onRelatedEventClicked = (evt: React.MouseEvent<HTMLElement>) => {
        evt.stopPropagation();
        const { meetingId, event, readingPaneEntrySource, insightsEntrySource } = props;
        lazyOpenEventInsight.importAndExecute(
            meetingId,
            event,
            readingPaneEntrySource,
            insightsEntrySource
        );
    };
    const onKeyDown = (evt: React.KeyboardEvent<HTMLElement>) => {
        if (evt.which === KeyCodes.enter) {
            const { meetingId, event, readingPaneEntrySource, insightsEntrySource } = props;
            lazyOpenEventInsight.importAndExecute(
                meetingId,
                event,
                readingPaneEntrySource,
                insightsEntrySource
            );
        }
    };

    const { referenceId, relevanceRelationship } = props.event;
    const isOrganizer = getIsOrganizerFromMeetingId(props.meetingId);
    const traceId = getInsightsTraceId(props.meetingId, props.insightsEntrySource);
    if (!traceId) {
        trace.warn('RelatedEvent: invalid traceId');
    }
    return (
        <div
            ref={relatedEventContainerRef}
            className={styles.relatedEventContainer}
            role="listitem"
            tabIndex={0}
            data-is-focusable="true" // needed for FocusZone management to work correctly
            aria-label={getEventInsightAriaLabel(props.event)}
            aria-posinset={props.position + 1}
            aria-setsize={props.renderedEventsCount}>
            <div className={styles.relatedEvent}>
                <div
                    className={styles.relatedEventContent}
                    onClick={onRelatedEventClicked}
                    onKeyDown={onKeyDown}>
                    {isFeatureEnabled('cal-meeting-insights-reasonMarkers')
                        ? renderReasonMarkerRow()
                        : renderFirstRow()}
                    {renderSecondRow()}
                    {renderThirdRow()}
                </div>
                <div
                    className={classNames(styles.feedbackHoverTarget)}
                    onClick={onFeedbackContainerClicked}>
                    <FeedbackContainer
                        colorScheme={props.colorScheme}
                        traceId={traceId}
                        feedbackType={FeedbackType.Event}
                        isOrganizer={isOrganizer}
                        relevanceRelationship={relevanceRelationship}
                        referenceId={referenceId}
                        entrySource={props.insightsEntrySource}
                        meetingId={props.meetingId}
                        itemId={props.event.id}
                    />
                </div>
            </div>
        </div>
    );
});

function onFeedbackContainerClicked(evt: React.MouseEvent<any>) {
    // Suppress the onClick callback of feedback UX, otherwise it'll open the file.
    // This is needed because feedbackHoverTarget needs to be part of relatedFile, so that it shows up upon hovering over the file content,
    evt.stopPropagation();
}
